import Cell from "./Cell";
import Vector3 from "./Utils/Vector3";
import Stack from "./Utils/Language/Stack";
import { randomBetween } from "./Utils/Language/SeededRandom";
import { DIRECTIONS } from "./Constants/Direction";
import Map3D from "./Map3D";
import Prototype from "./Prototype/Prototype";
import PrototypeManager from "./Prototype/PrototypeManager";

export default class WaveFunctionCollapse {
  /**
   * 3D Grid of the WFC
   * @type {Map3D}
   */
  private map: Map3D;

  /**
   * Stack of the cells to propagate
   * @type {Stack<Vector3>}
   */
  private stack: Stack<Vector3> = new Stack();

  /**
   * Number of collapsed cells
   * @type {number}
   */
  private nbCollapsed: number = 0;

  /**
   * Current iteration number
   * @type {number}
   */
  private iteration: number = 0;

  private readonly prototypeManager: PrototypeManager;

  // TODO : remove this after startFromBottom mode is stable ?
  private readonly startFromBottom: boolean;

  private customConstraintApplied: boolean = false;

  /**
   * @param prototypeManager
   * @param { Vector3 } mapSize
   * @param startFromBottom
   */
  constructor(
    prototypeManager: PrototypeManager,
    mapSize: Vector3,
    startFromBottom: boolean = true,
  ) {
    this.prototypeManager = prototypeManager;
    this.map = new Map3D(mapSize, prototypeManager);
    this.startFromBottom = startFromBottom;
  }

  //// Main Methods : Public

  /**
   * Start the WFC
   * @param {boolean} isManualIteration
   * @returns {string[][][]} map
   */
  public start(isManualIteration = false): Cell[][][] {
    try {
      if (!this.customConstraintApplied) {
        this.customConstraintIteration(); // Apply custom constraints
        this.customConstraintApplied = true;
      }

      this.automaticIterations();
    } catch (error) {
      console.error("WFC error", error);
      throw new Error("WFC error");
    }
    return this.map.map;
  }

  /**
   * Start automatic iterations
   */
  public startStepIteration(): { map: Cell[][][]; collapsedCell: Cell } {
    try {
      if (!this.customConstraintApplied) {
        this.customConstraintIteration(); // Apply custom constraints
        this.customConstraintApplied = true;
      }
      const collapsedCell = this.manualIterations();
      return { map: this.map.map, collapsedCell: collapsedCell! };
    } catch (error) {
      console.error("WFC error", error);
      throw new Error("WFC error");
    }
  }

  /**
   * Reset the WFC
   */
  public reset(): void {
    this.map.clear();
    this.stack.clear();
    this.nbCollapsed = 0;
    this.iteration = 0;
    this.customConstraintApplied = false;
  }

  /**
   * Check if the WFC is finished
   */
  public isFinished(): boolean {
    return this.isFullyCollapsed();
  }

  //// Main Methods : Iterations

  /**
   * Iterate
   */
  private iterate(): Cell {
    const coords = this.getMinEntropyCoords();
    const collaspedCell = this.collapse(coords);
    this.propagate(coords);
    return collaspedCell;
  }

  /**
   * Automatic iterations
   */
  private automaticIterations() {
    while (this.startFromBottom && !this.isBottomBorderFullyCollapsed()) {
      this.bottomBorderIteration(false);
    }
    while (!this.isFullyCollapsed()) {
      this.iterate();
    }
  }

  /**
   * Single iteration
   */
  private manualIterations(): Cell | undefined {
    if (this.startFromBottom && !this.isBottomBorderFullyCollapsed()) {
      this.bottomBorderIteration(true);
    } else if (!this.isFullyCollapsed()) {
      return this.iterate();
    }
  }

  /**
   * Side border iteration
   * Collapse the cells on the side border of the map
   */
  private sideBorderIteration(): void {
    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let y = 0; y < this.map.mapSize.y; y++) {
        for (let z = 0; z < this.map.mapSize.z; z++) {
          const coords = new Vector3(x, y, z);
          if (this.isOnSideBorder(coords) || this.isOnTopBorder(coords)) {
            this.collapse(coords, true);
            this.propagate(coords);
          }
        }
      }
    }
  }

  /**
   * Bottom border iteration
   * @param single iteration or all
   */
  private bottomBorderIteration(
    single: boolean = false,
    blank: boolean = false,
  ): void {
    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let z = 0; z < this.map.mapSize.z; z++) {
        const coords = new Vector3(x, 0, z);
        if (this.isOnBottomBorder(coords) && !this.getCell(coords).collapsed) {
          this.collapse(coords, blank);
          this.propagate(coords);
          if (single) return; // TODO : not clean
        }
      }
    }
  }

  // TODO : factorize this method
  private customConstraintIteration() {
    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let y = 0; y < this.map.mapSize.y; y++) {
        for (let z = 0; z < this.map.mapSize.z; z++) {
          const coords = new Vector3(x, y, z);
          const cell = this.getCell(coords);

          // Top border
          if (y === this.map.mapSize.y - 1) {
            for (let proto of cell.possiblePrototypes) {
              // Remove prototypes that cannot have a blank cell on top
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.posY)
                  .includes(this.prototypeManager.blankId)
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          // Not Bottom border
          if (y > 0) {
            for (let proto of cell.possiblePrototypes) {
              if (
                proto.constrain_to === "bot" /* TODO : hardcoded constraint */
              ) {
                cell.constrain(proto.id);
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          // Not Top border
          if (y < this.map.mapSize.y - 1) {
            for (let proto of cell.possiblePrototypes) {
              if (
                proto.constrain_to === "top" /* TODO : hardcoded constraint */
              ) {
                cell.constrain(proto.id);
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          // Bottom border
          if (y === 0) {
            for (let proto of cell.possiblePrototypes) {
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.negY)
                  .includes(this.prototypeManager.blankId) ||
                proto.constrain_from === "bot" /* TODO : hardcoded constraint */
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          if (x === this.map.mapSize.x - 1) {
            for (let proto of cell.possiblePrototypes) {
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.posX)
                  .includes(this.prototypeManager.blankId)
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          if (x === 0) {
            for (let proto of cell.possiblePrototypes) {
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.negX)
                  .includes(this.prototypeManager.blankId)
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          if (z === this.map.mapSize.z - 1) {
            for (let proto of cell.possiblePrototypes) {
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.posZ)
                  .includes(this.prototypeManager.blankId)
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }

          if (z === 0) {
            for (let proto of cell.possiblePrototypes) {
              if (
                !proto
                  .getPossiblePrototypeIdsInDirection(DIRECTIONS.negZ)
                  .includes(this.prototypeManager.blankId)
              ) {
                cell.constrain(proto.id); // TODO : use this.constraint ?
                if (!this.stack.includes(coords)) {
                  this.stack.push(coords);
                }
              }
            }
          }
        }
      }
    }

    this.propagate(null);
  }

  //// Main methods : WFC

  /**
   * Collapse the cell at coords
   * @param coords
   * @param isBlank
   * @returns {Cell} collapsed cell
   */
  private collapse(
    coords: Vector3,
    isBlank: boolean = false,
    constraint: string | null = null,
  ): Cell {
    return this.getCell(coords).collapse(
      isBlank ? this.prototypeManager.blankId : null,
    );
    // TODO : remove return, debug only
  }

  /**
   * Remove the prototype from the list of possible prototypes
   * @param {Vector3} coords
   * @param {string} prototypeId
   */
  private constrain(coords: Vector3, prototypeId: string) {
    this.getCell(coords).constrain(prototypeId);
  }

  /**
   * @param {Vector3} coords
   */
  private propagate(coords: Vector3 | null): void {
    // console.log("Propagating from", coords);
    if (coords) this.stack.push(coords);

    while (!this.stack.isEmpty()) {
      // console.log("Stack not empty:", this.stack);

      const currentCoords: Vector3 | undefined = this.stack.pop();
      // console.log("Current coords", currentCoords);

      if (!currentCoords) throw new Error("Current coords is undefined");

      const directions = this.getNeighboursDirection(currentCoords);
      // console.log("Neighbours Directions", directions);

      for (const direction of directions) {
        // console.log("Direction", direction);
        const neighbourCoords = currentCoords.clone().add(direction);
        // console.log("Neighbour coords", neighbourCoords);

        const neighbourPossiblePrototypes =
          this.getPossiblePrototypes(neighbourCoords);
        // console.log(
        //   "Neighbour possible prototypes",
        //   neighbourPossiblePrototypes,
        // );

        if (neighbourPossiblePrototypes.length === 0) continue;

        // Must be called each time, because the possible prototypes can change
        const currentPossiblePrototypes = this.getPossiblePrototypesInDirection(
          currentCoords,
          direction,
        );
        // console.log("Current possible prototypes", currentPossiblePrototypes);

        // Compare the two lists
        for (const neighbourPossiblePrototype of neighbourPossiblePrototypes) {
          // console.log(
          //   "Neighbour possible prototype",
          //   neighbourPossiblePrototype,
          // );
          if (!currentPossiblePrototypes.includes(neighbourPossiblePrototype)) {
            // console.log(
            //   "Constraining",
            //   neighbourCoords,
            //   neighbourPossiblePrototype,
            // );
            this.constrain(neighbourCoords, neighbourPossiblePrototype);
            // console.log(
            //   "Constrained",
            //   neighbourCoords,
            //   neighbourPossiblePrototype,
            // );
            if (!this.stack.includes(neighbourCoords)) {
              this.stack.push(neighbourCoords);
            }
          }
        }
      }
    }
  }

  //// Collapse checkers methods

  /**
   * Returns true if all the cells on the side border of the map are collapsed
   * @Returns {boolean}
   */
  private areSideBordersCollapsed(): boolean {
    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let y = 0; y < this.map.mapSize.y; y++) {
        for (let z = 0; z < this.map.mapSize.z; z++) {
          const coords = new Vector3(x, y, z);
          if (this.isOnSideBorder(coords) && !this.getCell(coords).collapsed) {
            return false;
          }
        }
      }
    }
    return true;
  }

  /**
   * Returns true if all the cells on the bottom border of the map are collapsed
   * @Returns {boolean}
   */
  private isBottomBorderFullyCollapsed(): boolean {
    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let z = 0; z < this.map.mapSize.z; z++) {
        const coords = new Vector3(x, 0, z);
        if (this.isOnBottomBorder(coords) && !this.getCell(coords).collapsed) {
          return false;
        }
      }
    }
    return true;
  }

  /**
   * Checks if any cell has more than one possible prototype then the total number of collapsed cells
   * @returns {boolean} isFullyCollapsed
   */
  private isFullyCollapsed(): boolean {
    let newNbCollapsed = 0;
    let allCollapsed = true;

    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let y = 0; y < this.map.mapSize.y; y++) {
        for (let z = 0; z < this.map.mapSize.z; z++) {
          const cell = this.getCell(new Vector3(x, y, z));
          if (!cell.collapsed) {
            // One cell has more than one possible prototype
            // so the map is not fully collapsed
            allCollapsed = false;
          } else {
            newNbCollapsed++;
          }
        }
      }
    }

    this.nbCollapsed = newNbCollapsed;

    // All cells have only one possible prototype
    // so the map is fully collapsed
    return allCollapsed;
  }

  //// Getters Utils

  /**
   * Get the progress percentage of the WFC
   * @returns {number} progressPercentage
   */
  private progressPercentage(): number {
    return (
      (this.nbCollapsed /
        (this.map.mapSize.x * this.map.mapSize.y * this.map.mapSize.z)) *
      100
    );
  }

  /**
   * Get the cell with the minimum entropy (= the cell that contains the least number of possible prototypes)
   * If there are multiple cells with the same entropy, return a random one or use weights to choose one
   * @returns {Cell}
   */
  private getMinEntropy(): Cell {
    let minEntropy: number = Infinity;
    let minEntropyCells: Cell[] = [];

    for (let x = 0; x < this.map.mapSize.x; x++) {
      for (let y = 0; y < this.map.mapSize.y; y++) {
        for (let z = 0; z < this.map.mapSize.z; z++) {
          const cell = this.getCell(new Vector3(x, y, z));
          const cellEntropy = cell.getEntropy();

          if (!cell.collapsed && cellEntropy < minEntropy) {
            minEntropy = cellEntropy;
            minEntropyCells = [cell];
          } else if (!cell.collapsed && cellEntropy === minEntropy) {
            minEntropyCells.push(cell);
          }
        }
      }
    }

    const rnd = randomBetween(0, minEntropyCells.length - 1);

    // If there are multiple cells with the same entropy, return a random one
    // TODO : use weights to choose one
    return minEntropyCells[0];
  }

  /**
   * Get the coordinates of the cell with the minimum entropy
   * @returns {Vector3} coordinates
   */
  private getMinEntropyCoords(): Vector3 {
    return this.getMinEntropy().coordinates;
  }

  /**
   * Returns an array of directions (i.e. Vector3(1, 0, 0) for the right neighbour) that are valid for the given coords
   * @param {Vector3} coords
   * @returns {Vector3[]}
   */
  private getNeighboursDirection(coords: Vector3): Vector3[] {
    const possibleDirections = [];

    for (const direction of Object.values(DIRECTIONS)) {
      const directionCoords = coords.clone().add(direction);
      if (this.map.isCoordsValid(directionCoords)) {
        possibleDirections.push(direction);
      }
    }

    return possibleDirections;
  }

  /**
   * Return the list of possible prototypes for a cell, returns an empty array if the cell is collapsed
   * @param {Vector3} coords
   * @returns {string[]} possiblePrototypes
   */
  private getPossiblePrototypes(coords: Vector3): string[] {
    // if (this.isOnSideBorder(coords)) return [this.prototypeManager.blankId]; // TODO : Mur Debug
    return this.getCell(coords).possiblePrototypesIds;
  }

  /**
   * Get Cell from coords
   * @param {Vector3} coords
   * @returns {Cell}
   */
  private getCell(coords: Vector3): Cell {
    return this.map.getCellAt(coords);
  }

  /**
   * Get the possible prototypes in the given direction
   *
   * @param {Vector3} coords
   * @param {Vector3} direction
   * @returns {String[]} prototype ids
   */
  private getPossiblePrototypesInDirection(
    coords: Vector3,
    direction: Vector3,
  ): string[] {
    const cell: Cell = this.getCell(coords);
    const possiblePrototypeIds: string[] = [];

    // If the cell is collapsed, push the collapsed cell prototype id only
    if (cell.collapsed) {
      possiblePrototypeIds.push(cell.collapsedCellPrototypeId);
    } else {
      possiblePrototypeIds.push(...cell.possiblePrototypesIds);
    }

    const possiblePrototypeIdsInDirectionOutput: string[] = [];

    for (const possiblePrototypeId of possiblePrototypeIds) {
      const prototype: Prototype =
        this.prototypeManager.getPrototypeById(possiblePrototypeId);

      const possiblePrototypeIdsInDirection: string[] =
        prototype.getPossiblePrototypeIdsInDirection(direction);

      // Add the possible prototypes in the direction to the output only once
      possiblePrototypeIdsInDirection.forEach((p) => {
        if (!possiblePrototypeIdsInDirectionOutput.includes(p)) {
          possiblePrototypeIdsInDirectionOutput.push(p);
        }
      });
    }

    return possiblePrototypeIdsInDirectionOutput;
  }

  //// Borders checker methods

  /**
   * Returns true if the cell is on the side border of the map
   * @param {Vector3} coords
   * @returns {boolean}
   */
  private isOnSideBorder(coords: Vector3): boolean {
    return this.isOnXSideBorder(coords) || this.isOnZSideBorder(coords);
  }

  /**
   * Returns true if the cell is on the X side border of the map
   * @param {Vector3} coords
   * @returns {boolean}
   */
  private isOnXSideBorder(coords: Vector3): boolean {
    return coords.x === 0 || coords.x === this.map.mapSize.x - 1;
  }

  /**
   * Returns true if the cell is on the Z side border of the map
   * @param {Vector3} coords
   * @returns {boolean}
   */
  private isOnZSideBorder(coords: Vector3): boolean {
    return coords.z === 0 || coords.z === this.map.mapSize.z - 1;
  }

  /**
   * Returns true if the cell is on the top border of the map
   * @param {Vector3} coords
   * @returns {boolean}
   */
  private isOnTopBorder(coords: Vector3): boolean {
    return coords.y === this.map.mapSize.y - 1;
  }

  /**
   * Returns true if the cell is on the bottom border of the map
   * @param {Vector3} coords
   * @returns {boolean}
   */
  private isOnBottomBorder(coords: Vector3): boolean {
    return coords.y === 0;
  }
}
